import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ICompany } from '../../companies/models/company.interface';
import { ERoleAdminString } from '../../../shared/data/role.enum';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { CompanyService } from '../../companies/services/company.service';
import { ToastService } from '../../../shared/services/toast.service';
import { SetupService } from '../services/setup.service';
import { ModuleService } from '../services/module.service';
import { IModuleForm, IModuleGraphConfig } from '../models/setup.interface';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, forkJoin, map, Observable, of, retry, Subscription, throwError, timer } from 'rxjs';
import { ThemeOptions, YesNoOptions } from '../models/select-options-config-setup';
import { NAVIGATION_CONFIG } from '../components/setting-module/enum/enum-type';
import { EMessageType } from '../../../shared/data/response-default.interface';
import { ModalConfirmacaoService } from '../../../shared/services/modal-confirmacao.service';
import { IModuleDraft, SetupModuleDraftService } from '../services/setup-module-draft.service';
import { SessionService } from '../../../shared/services/session.service';

// Falhas que costumam passar sozinhas: sem conexão (0), gateway (502), serviço
// indisponível (503) e timeout (504). Um 500 costuma ser erro no código do backend e
// falharia igual de novo, então não é repetido.
const TRANSIENT_HTTP_STATUSES = [0, 502, 503, 504];
const FLAGS_RETRY_COUNT = 2;
const FLAGS_RETRY_DELAY_MS = 1000;

interface IModuleFlagsFailure {
  moduleName: string;
  reason: string;
}
import {
  getMissingDependencies,
  getModulesDependingOn,
  getRequiredModuleIndex,
  getSelectedModuleRoutes,
  getUnselectedModuleRoutes,
  isRequiredByFlow,
  MODULE_DISPLAY_NAMES,
  REQUIRED_FLOW_MODULES
} from '../models/module-dependencies.const';
import {
  createRegistrationGroup,
  getAgeGenderModuleNames,
  registrationFromConfig
} from '../components/setup-identification/setup-identification';

@Component({
  selector: 'app-edit-setup',
  templateUrl: './edit-setup.component.html',
  styleUrls: ['./edit-setup.component.scss'],
  providers: [SetupModuleDraftService]
})
export class EditSetupComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  readonly anonymousControl = new UntypedFormControl(false);
  readonly registrationGroup = createRegistrationGroup();
  formSetup = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    companyId: new UntypedFormControl(null, [Validators.required]),
    setupConfig: new UntypedFormGroup({
      setupTheme: new UntypedFormControl(null, [Validators.required]),
      checkValidPayment: new UntypedFormControl(false),
      hasTelemedicine: new UntypedFormControl(false),
      allowAnonymousUsers: this.anonymousControl,
      registration: this.registrationGroup,
      accessibilityVoice: new UntypedFormControl(false),
      home: new UntypedFormControl(null),
      // "Ainda está aí?" no app (OE-1887). Vazio usa o padrão do app: 60s e 15s.
      idleTimeoutS: new UntypedFormControl(null, [Validators.min(1)]),
      warningCountdownS: new UntypedFormControl(null, [Validators.min(1)])
    })
  });

  companies: ICompany[] = [];
  modules: IModuleForm[] = [];
  availableModules: IModuleForm[] = [];
  homeModuleOptions: IModuleForm[] = [];
  ageGenderModuleNames: string[] = [];
  private allModules: IModuleForm[] = [];
  filteredModules: IModuleForm[] = [];

  optionsTheme = ThemeOptions;
  yesNoOptions = YesNoOptions;

  setupId = '';
  private moduleConfigNavigationSub?: Subscription;
  private moduleBeingConfiguredId: string | null = null;
  private persistedModuleIds = new Set<string>();
  // true do clique em Salvar até o fim do PUT e das chamadas de flags: evita um segundo
  // salvamento em paralelo enquanto o retry ainda está em andamento.
  saving = false;

  constructor(
    private readonly modulesService: ModuleService,
    private readonly setupService: SetupService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalConfirmacao: ModalConfirmacaoService,
    private readonly moduleDraft: SetupModuleDraftService,
    private readonly sessionService: SessionService
  ) {}

  get hasUnsavedModules(): boolean {
    return this.moduleDraft.entries().length > 0;
  }

  ngOnInit(): void {
    this.setupId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';

    // Só Admin escolhe a empresa; para os demais a chamada (que exige company.list) derrubaria o forkJoin.
    const companies$ =
      this.sessionService.getSession()?.role === ERoleAdminString.Admin
        ? this.companyService.listarEmpresasAtivas()
        : of({ data: [] as ICompany[] });

    forkJoin([companies$, this.modulesService.getAll()]).subscribe(([companiesRes, modulesRes]) => {
      this.companies = companiesRes.data;
      this.allModules = modulesRes.data.map((m) => ({
        ...m,
        hasConfigModule: this.hasConfig(m.configSchema)
      })) as IModuleForm[];

      if (this.setupId) {
        this.setupService.getSetupById(this.setupId).subscribe((response) => {
          if (response.isSucceed) {
            this.formSetup.get('name')?.setValue(response.data.name);
            this.formSetup.get('companyId')?.setValue(response.data.companyId);
            this.formSetup.get('setupConfig.home')?.setValue(response.data.setupConfig?.home ?? null);
            this.formSetup.get('setupConfig.setupTheme')?.setValue(response.data.setupConfig?.setupTheme);
            this.formSetup.get('setupConfig.checkValidPayment')?.setValue(response.data.setupConfig?.checkValidPayment);
            this.formSetup.get('setupConfig.hasTelemedicine')?.setValue(response.data.setupConfig?.hasTelemedicine);
            this.anonymousControl.setValue(response.data.setupConfig?.allowAnonymousUsers === true);
            this.registrationGroup.setValue(registrationFromConfig(response.data.setupConfig));
            this.formSetup
              .get('setupConfig.accessibilityVoice')
              ?.setValue(response.data.setupConfig?.accessibilityVoice ?? false);
            this.formSetup.get('setupConfig.idleTimeoutS')?.setValue(response.data.setupConfig?.idleTimeoutS ?? null);
            this.formSetup
              .get('setupConfig.warningCountdownS')
              ?.setValue(response.data.setupConfig?.warningCountdownS ?? null);

            const allModulesMap = new Map(this.allModules.map((m) => [m.id, m]));
            this.modules = response.data.modules
              .map((setupModule) => {
                const fullModuleInfo = allModulesMap.get(setupModule.id);
                if (!fullModuleInfo) {
                  return undefined;
                }
                return {
                  ...fullModuleInfo,
                  configModule: { ...this.getDefaultConfig(), ...setupModule.configModule },
                  requiresAuth: setupModule.requiresAuth,
                  requiresTriage: setupModule.requiresTriage
                } as IModuleForm;
              })
              .filter((m): m is IModuleForm => m !== undefined);

            const selectedModuleIds = new Set(this.modules.map((m) => m.id));
            this.persistedModuleIds = selectedModuleIds;
            this.availableModules = this.allModules.filter((m) => !selectedModuleIds.has(m.id));
            this.updateHomeModuleOptions();
            this.updateDisplayConnections();
          } else {
            this.availableModules = [...this.allModules];
          }
        });
      }

      this.cdr.detectChanges();
    });

    this.listenToModuleConfigNavigation();
  }

  ngOnDestroy(): void {
    this.moduleConfigNavigationSub?.unsubscribe();
  }

  public editSetup(): void {
    if (this.formSetup.invalid) {
      this.toastService.toast('Preencha todos os campos obrigatórios antes de salvar.');
      this.formSetup.markAllAsTouched();
      return;
    }

    if (this.modules.length === 0) {
      this.toastService.toast('O setup deve ter ao menos um módulo.');
      return;
    }

    if (!this.validateModuleDependencies()) {
      return;
    }

    const modulesPayload = this.modules.map((module, index) => ({
      moduleId: module.id,
      order: index + 1,
      configModule: JSON.stringify(module.configModule)
    }));

    const updateSetupPayload = {
      ...this.formSetup.getRawValue(),
      modules: modulesPayload
    };

    this.saving = true;

    this.setupService.editSetup(this.setupId, updateSetupPayload).subscribe({
      next: (response) => {
        if (response.isSucceed) {
          this.saveDraftModuleFlags();
        } else {
          this.saving = false;
          this.toastService.toast('Erro ao salvar o setup.');
        }
      },
      error: (err) => {
        this.saving = false;
        console.error(err);
        this.toastService.toast('Ocorreu um erro inesperado ao salvar. Tente novamente.');
      }
    });
  }

  drop(event: CdkDragDrop<IModuleForm[]>): void {
    moveItemInArray(this.modules, event.previousIndex, event.currentIndex);
    this.updateHomeModuleOptions();
    this.updateDisplayConnections();
  }

  addModule(moduleToAdd: IModuleForm): void {
    this.insertModule(moduleToAdd, this.modules.length);
    this.addRequiredModules();
    this.updateHomeModuleOptions();
  }

  private insertModule(moduleToAdd: IModuleForm, index: number): void {
    this.availableModules = this.availableModules.filter((m) => m.id !== moduleToAdd.id);
    const configModule = this.getDefaultConfig();
    this.modules.splice(index, 0, {
      ...moduleToAdd,
      checked: true,
      configModule
    });

    // Módulo que ainda não está no setup salvo: a tela de configuração usa o rascunho,
    // porque o backend só conhece o módulo depois do PUT do setup.
    if (!this.persistedModuleIds.has(moduleToAdd.id)) {
      this.moduleDraft.add(moduleToAdd.id, {
        configModule: { ...configModule },
        requiresAuth: moduleToAdd.requiresAuth ?? false,
        requiresTriage: moduleToAdd.requiresTriage ?? false
      });
    }
  }

  // Insere no fluxo o Auth e a Triagem que algum módulo do fluxo requer e que ainda não estão nele.
  private addRequiredModules(): void {
    REQUIRED_FLOW_MODULES.forEach(({ route, flag }) => {
      const requiredModule = this.availableModules.find((m) => m.route === route);
      if (requiredModule && isRequiredByFlow(this.modules, flag)) {
        this.insertModule(
          requiredModule,
          getRequiredModuleIndex(
            route,
            this.modules.map((m) => m.route)
          )
        );
      }
    });
  }

  getPossibleConnections(currentModule: IModuleForm, currentIndex: number): IModuleForm[] {
    const connections = currentModule.configModule?.connections || [];
    const connectedIds = new Set(connections);

    const existingConnections = this.modules.filter((m) => connectedIds.has(m.id));

    let potentialNewConnections: IModuleForm[] = [];

    if (currentModule.multipleConnections === false && connections.length >= 1) {
      potentialNewConnections = [];
    } else {
      potentialNewConnections = this.modules.filter((optionModule, optionIndex) => {
        return optionIndex > currentIndex || optionModule.multipleConnections === true;
      });
    }

    const finalOptions = [...existingConnections, ...potentialNewConnections];

    const finalOptionsMap = new Map<string, IModuleForm>();
    finalOptions.forEach((module) => {
      if (module.id !== currentModule.id) {
        finalOptionsMap.set(module.id, module);
      }
    });

    return Array.from(finalOptionsMap.values());
  }

  removeModule(moduleToRemove: IModuleForm): void {
    const dependents = getModulesDependingOn(moduleToRemove.route).filter((depRoute) =>
      this.modules.some((m) => m.route === depRoute)
    );

    if (dependents.length > 0) {
      const dependentNames = dependents.map((r) => MODULE_DISPLAY_NAMES[r] || r).join(', ');
      this.modalConfirmacao
        .abrirModalConfirmacao({
          mensagem: `O módulo "${moduleToRemove.name}" é obrigatório para: ${dependentNames}. Deseja remover mesmo assim?`,
          textoBotaoConfirma: 'Remover',
          textoBotaoCancela: 'Cancelar'
        })
        .subscribe((confirmed) => {
          if (confirmed) {
            this.executeRemoveModule(moduleToRemove);
          }
        });
      return;
    }

    this.executeRemoveModule(moduleToRemove);
  }

  // A posição é buscada na hora: a remoção pode ter esperado o modal de dependência, e nesse meio
  // tempo outros módulos podem ter saído do fluxo.
  private executeRemoveModule(moduleToRemove: IModuleForm): void {
    const index = this.modules.findIndex((m) => m.id === moduleToRemove.id);
    if (index === -1) return;
    this.modules.splice(index, 1);
    this.moduleDraft.remove(moduleToRemove.id);
    const originalModule = this.allModules.find((m) => m.id === moduleToRemove.id);
    if (originalModule) {
      this.availableModules.push(originalModule);
      this.availableModules.sort((a, b) => a.name.localeCompare(b.name));
    }
    this.modules.forEach((m) => {
      if (m.configModule?.connections) {
        m.configModule.connections = m.configModule.connections.filter((id: string) => id !== moduleToRemove.id);
      }
    });
    this.updateHomeModuleOptions();
    this.updateDisplayConnections();
  }

  private validateModuleDependencies(): boolean {
    const presentRoutes = this.modules.map((m) => m.route);

    for (const module of this.modules) {
      const missing = getMissingDependencies(module.route, presentRoutes);
      if (missing.length > 0) {
        const missingNames = missing.map((r) => MODULE_DISPLAY_NAMES[r] || r).join(', ');
        this.toastService.toast(
          `O módulo "${module.name}" requer: ${missingNames}. Adicione-os antes de salvar.`,
          EMessageType.Warning
        );
        return false;
      }
    }
    return true;
  }

  hasUnmetDependencies(module: IModuleForm): boolean {
    const presentRoutes = this.modules.map((m) => m.route);
    return getMissingDependencies(module.route, presentRoutes).length > 0;
  }

  getMissingDependencyNames(module: IModuleForm): string[] {
    const presentRoutes = this.modules.map((m) => m.route);
    return getMissingDependencies(module.route, presentRoutes).map((r) => MODULE_DISPLAY_NAMES[r] || r);
  }

  private updateHomeModuleOptions(): void {
    this.homeModuleOptions = this.modules.filter((m) => m.multipleConnections === true);
    this.ageGenderModuleNames = getAgeGenderModuleNames(this.modules);

    const homeControl = this.formSetup.get('setupConfig.home');
    if (homeControl && homeControl.value) {
      const selectedHomeStillExists = this.homeModuleOptions.some((m) => m.id === homeControl.value);
      if (!selectedHomeStillExists) {
        homeControl.setValue(null);
      }
    }
  }

  toggleConnection(parentModule: IModuleForm, childModuleId: string): void {
    const parentConfig = parentModule.configModule as IModuleGraphConfig;
    const isAlreadyConnected = parentConfig.connections.includes(childModuleId);

    const childModule = this.modules.find((m) => m.id === childModuleId);
    if (!childModule) {
      return;
    }

    if (isAlreadyConnected) {
      parentConfig.connections = parentConfig.connections.filter((id) => id !== childModuleId);

      if (childModule.configModule!.previousModule === parentModule.id) {
        childModule.configModule!.previousModule = null;
      }
    } else {
      parentConfig.connections.push(childModuleId);
      childModule.configModule!.previousModule = parentModule.id;
    }

    this.updateDisplayConnections();
  }

  isConnected(parentModule: IModuleForm, childModuleId: string): boolean {
    return parentModule.configModule?.connections?.includes(childModuleId) ?? false;
  }

  hasConfig(obj: any): boolean {
    return (
      !!obj &&
      Object.keys(obj).filter(([key, value]) => value !== null && !Object.values(NAVIGATION_CONFIG).includes(key))
        .length > 0
    );
  }

  private updateDisplayConnections(): void {
    const allModulesMap = new Map(this.allModules.map((m) => [m.id, m]));
    this.modules.forEach((parentModule) => {
      const config = parentModule.configModule as IModuleGraphConfig;
      if (config.connections.length > 0) {
        parentModule.displayConnections = config.connections
          .map((id) => allModulesMap.get(id))
          .filter((m): m is IModuleForm => !!m);
      } else {
        parentModule.displayConnections = [];
      }
    });
  }

  private getDefaultConfig(): IModuleGraphConfig {
    return { canReturn: false, connections: [], previousModule: null };
  }

  setFilteredModules(currentModule: IModuleForm): void {
    this.filteredModules = this.modules.filter((m) => m.id !== currentModule.id);
  }

  private listenToModuleConfigNavigation(): void {
    this.moduleConfigNavigationSub = this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd =>
            event instanceof NavigationStart || event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          const moduleId = this.extractModuleId(event.url);
          if (moduleId) {
            this.moduleBeingConfiguredId = moduleId;
          }
        }

        if (
          event instanceof NavigationEnd &&
          this.moduleBeingConfiguredId &&
          this.isBaseEditRoute(event.urlAfterRedirects)
        ) {
          const moduleId = this.moduleBeingConfiguredId;
          this.moduleBeingConfiguredId = null;
          this.refreshModuleConfig(moduleId);
        }
      });
  }

  private refreshModuleConfig(moduleId: string): void {
    if (!this.setupId) {
      return;
    }

    const moduleToUpdate = this.modules.find((m) => m.id === moduleId);
    if (!moduleToUpdate) {
      return;
    }

    const draft = this.moduleDraft.get(moduleId);
    if (draft) {
      this.applyModuleConfig(moduleToUpdate, draft.configModule, draft);
      return;
    }

    this.setupService.getConfigModule(this.setupId, moduleId).subscribe({
      next: (response) => {
        const updatedConfig = response?.data?.configModule;
        if (!updatedConfig) {
          return;
        }

        this.applyModuleConfig(moduleToUpdate, updatedConfig, {
          requiresAuth: response.data.requiresAuth ?? moduleToUpdate.requiresAuth,
          requiresTriage: response.data.requiresTriage ?? moduleToUpdate.requiresTriage
        });
      },
      error: (err) => {
        console.error('Erro ao atualizar configuração do módulo:', err);
      }
    });
  }

  // As conexões e o módulo anterior são editados nesta tela, então prevalecem sobre os
  // valores que vêm da tela de configuração. Os flags vêm de lá e atualizam os ícones da lista.
  private applyModuleConfig(
    moduleToUpdate: IModuleForm,
    updatedConfig: Record<string, any>,
    flags: Pick<IModuleForm, 'requiresAuth' | 'requiresTriage'>
  ): void {
    moduleToUpdate.requiresAuth = flags.requiresAuth;
    moduleToUpdate.requiresTriage = flags.requiresTriage;

    const currentConnections = Array.isArray(moduleToUpdate.configModule?.connections)
      ? [...moduleToUpdate.configModule!.connections]
      : [];
    const currentPreviousModule = moduleToUpdate.configModule?.previousModule ?? null;
    const previousConfig = moduleToUpdate.configModule;

    moduleToUpdate.configModule = {
      ...moduleToUpdate.configModule,
      ...updatedConfig,
      connections: currentConnections,
      previousModule: currentPreviousModule
    };

    this.removeUnselectedModulesFromFlow(moduleToUpdate, previousConfig, moduleToUpdate.configModule);
    this.addSelectedModulesToFlow(moduleToUpdate, updatedConfig);
    // Os flags podem ter mudado na tela de configuração sem nenhum módulo novo no fluxo.
    this.addRequiredModules();
    this.updateHomeModuleOptions();
    this.updateDisplayConnections();
    this.cdr.detectChanges();
  }

  private addSelectedModulesToFlow(module: IModuleForm, config: Record<string, unknown>): void {
    getSelectedModuleRoutes(module.configSchema, config).forEach((route) => {
      const moduleToAdd = this.availableModules.find((m) => m.route === route);
      if (moduleToAdd) {
        this.addModule(moduleToAdd);
      }
    });
  }

  private removeUnselectedModulesFromFlow(
    module: IModuleForm,
    previousConfig: Record<string, unknown> | null | undefined,
    newConfig: Record<string, unknown>
  ): void {
    getUnselectedModuleRoutes(module.configSchema, previousConfig, newConfig).forEach((route) => {
      const moduleToRemove = this.modules.find((m) => m.route === route);
      if (moduleToRemove) {
        this.removeModule(moduleToRemove);
      }
    });
  }

  // O PUT do setup ignora requiresAuth/requiresTriage e aplica o padrão global do módulo aos
  // módulos novos. Os valores escolhidos no rascunho seguem por updateModuleFlags, depois que o
  // PUT criou o módulo no setup.
  private saveDraftModuleFlags(): void {
    const flagRequests = this.moduleDraft
      .entries()
      .filter(([moduleId, draft]) => {
        const globalModule = this.allModules.find((m) => m.id === moduleId);
        return (
          draft.requiresAuth !== (globalModule?.requiresAuth ?? false) ||
          draft.requiresTriage !== (globalModule?.requiresTriage ?? false)
        );
      })
      .map(([moduleId, draft]) => this.saveModuleFlags(moduleId, draft));

    if (flagRequests.length === 0) {
      this.finishSave();
      return;
    }

    forkJoin(flagRequests).subscribe((results) => {
      const failures = results.filter((result): result is IModuleFlagsFailure => result !== null);

      if (failures.length === 0) {
        this.finishSave();
      } else {
        this.finishSaveWithFlagsError(failures);
      }
    });
  }

  // Emite null quando deu certo, ou a falha do módulo. Nunca emite erro: o forkJoin precisa
  // do resultado de todos os módulos para dizer quais falharam.
  private saveModuleFlags(moduleId: string, draft: IModuleDraft): Observable<IModuleFlagsFailure | null> {
    const moduleName = this.allModules.find((m) => m.id === moduleId)?.name ?? moduleId;
    const flags = { requiresAuth: draft.requiresAuth, requiresTriage: draft.requiresTriage };

    return this.setupService.updateModuleFlags(this.setupId, moduleId, flags).pipe(
      retry({
        count: FLAGS_RETRY_COUNT,
        delay: (error: unknown) =>
          this.isTransientError(error) ? timer(FLAGS_RETRY_DELAY_MS) : throwError(() => error)
      }),
      map((response) => {
        if (response.isSucceed) {
          return null;
        }

        const reason = response.messages?.[0]?.description || 'o servidor recusou a alteração';
        console.error('Setup salvo, mas updateModuleFlags retornou isSucceed=false', {
          setupId: this.setupId,
          moduleId,
          flags,
          messages: response.messages
        });
        return { moduleName, reason };
      }),
      catchError((error: unknown) => {
        console.error('Setup salvo, mas updateModuleFlags falhou', {
          setupId: this.setupId,
          moduleId,
          flags,
          error
        });
        return of({ moduleName, reason: this.describeHttpError(error) });
      })
    );
  }

  private isTransientError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && TRANSIENT_HTTP_STATUSES.includes(error.status);
  }

  private describeHttpError(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return 'erro inesperado';
    }

    return error.status === 0 ? 'sem conexão com o servidor' : `erro HTTP ${error.status}`;
  }

  private finishSave(): void {
    this.toastService.toast('Setup salvo com sucesso!');
    this.router.navigateByUrl('/dash/setups');
  }

  // A mensagem fica na tela até o usuário fechar: ela aparece já na listagem, depois da
  // navegação, e precisa dar tempo de ler qual módulo falhou e por quê.
  private finishSaveWithFlagsError(failures: IModuleFlagsFailure[]): void {
    const failedModules = failures.map((failure) => `${failure.moduleName} (${failure.reason})`).join(', ');

    this.toastService.toast(
      `O setup foi salvo, mas "Requer autenticação" e "Requer triagem" não foram salvos em: ${failedModules}. ` +
        'Esses campos ficaram com o valor padrão.',
      EMessageType.Warning,
      { disableTimeOut: true, closeButton: true }
    );
    this.router.navigateByUrl('/dash/setups');
  }

  private extractModuleId(url: string): string | null {
    const match = url.match(/\/module\/([^\/\?]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  private isBaseEditRoute(url: string): boolean {
    if (!this.setupId) {
      return false;
    }

    return url.startsWith(`/dash/setups/editar/${this.setupId}`) && !url.includes('/module/');
  }
}
